import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from "react";
import { invoiceService } from "@/services";
import { InvoiceDto } from "@shared/types/InvoiceDto";
import type { PaymentStatusResponse } from "@/services/InvoiceService";

const POLL_INTERVAL_MS = 5000;
const POLL_MAX_DURATION_MS = 120000;

export type PollState = "polling" | "paid" | "timeout" | "error";

export interface PollEntry {
  invoiceId: string;
  state: PollState;
  startedAt: number;
  result: PaymentStatusResponse | null;
  error: string | null;
  isDeposit?: boolean;
}

interface StripeInvoiceContextType {
  invoices: InvoiceDto[];
  isLoading: boolean;
  error: string | null;
  fetchInvoices: () => Promise<void>;
  downloadInvoice: (invoiceId: string) => Promise<void>;
  isDownloading: string | null;
  pollPaymentStatus: (invoiceId: string, isDeposit?: boolean) => void;
  dismissPoll: (invoiceId: string) => void;
  activePolls: PollEntry[];
}

const StripeInvoiceContext = createContext<StripeInvoiceContextType | null>(
  null
);

export function StripeInvoiceProvider({ children }: { children: ReactNode }) {
  const [invoices, setInvoices] = useState<InvoiceDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const [activePolls, setActivePolls] = useState<PollEntry[]>([]);
  const intervalsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  useEffect(() => {
    return () => {
      intervalsRef.current.forEach((interval) => clearInterval(interval));
      intervalsRef.current.clear();
    };
  }, []);

  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await invoiceService.getInvoices();
      setInvoices(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch invoices");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const downloadInvoice = useCallback(async (invoiceId: string) => {
    setIsDownloading(invoiceId);
    setError(null);
    try {
      const { hostedInvoiceUrl } = await invoiceService.getInvoiceUrl(
        invoiceId
      );
      window.open(hostedInvoiceUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to download invoice"
      );
    } finally {
      setIsDownloading(null);
    }
  }, []);

  const stopPolling = useCallback((invoiceId: string) => {
    const interval = intervalsRef.current.get(invoiceId);
    if (interval) {
      clearInterval(interval);
      intervalsRef.current.delete(invoiceId);
    }
  }, []);

  const pollPaymentStatus = useCallback((invoiceId: string, isDeposit?: boolean) => {
    if (intervalsRef.current.has(invoiceId)) {
      return;
    }

    const entry: PollEntry = {
      invoiceId,
      state: "polling",
      startedAt: Date.now(),
      result: null,
      error: null,
      isDeposit,
    };

    setActivePolls((prev) => {
      const filtered = prev.filter((p) => p.invoiceId !== invoiceId);
      return [...filtered, entry];
    });

    const tick = async () => {
      const elapsed = Date.now() - entry.startedAt;

      if (elapsed >= POLL_MAX_DURATION_MS) {
        stopPolling(invoiceId);
        setActivePolls((prev) =>
          prev.map((p) =>
            p.invoiceId === invoiceId
              ? { ...p, state: "timeout" as PollState }
              : p
          )
        );
        return;
      }

      try {
        const status = await invoiceService.getPaymentStatus(invoiceId);

        if (status.isPaid) {
          stopPolling(invoiceId);
          setActivePolls((prev) =>
            prev.map((p) =>
              p.invoiceId === invoiceId
                ? { ...p, state: "paid" as PollState, result: status }
                : p
            )
          );
        }
      } catch (err) {
        stopPolling(invoiceId);
        setActivePolls((prev) =>
          prev.map((p) =>
            p.invoiceId === invoiceId
              ? {
                  ...p,
                  state: "error" as PollState,
                  error: err instanceof Error ? err.message : "Poll failed",
                }
              : p
          )
        );
      }
    };

    tick();

    const interval = setInterval(tick, POLL_INTERVAL_MS);
    intervalsRef.current.set(invoiceId, interval);
  }, [stopPolling]);

  const dismissPoll = useCallback((invoiceId: string) => {
    stopPolling(invoiceId);
    setActivePolls((prev) => prev.filter((p) => p.invoiceId !== invoiceId));
  }, [stopPolling]);

  return (
    <StripeInvoiceContext.Provider
      value={{
        invoices,
        isLoading,
        error,
        fetchInvoices,
        downloadInvoice,
        isDownloading,
        pollPaymentStatus,
        dismissPoll,
        activePolls,
      }}
    >
      {children}
    </StripeInvoiceContext.Provider>
  );
}

export function useStripeInvoices() {
  const context = useContext(StripeInvoiceContext);
  if (!context) {
    throw new Error(
      "useStripeInvoices must be used within a StripeInvoiceProvider"
    );
  }
  return context;
}
